import { formatTime } from "@planner/core";
import type { CapabilitySnapshot, Locale, ThemeMode, TranslationKey } from "@planner/core";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { PersonPhoto } from "../components/PersonPhoto.js";
import { MinwalMark } from "../components/Brand.js";
import { Icon } from "./icons.js";
import { InstallBar } from "./Install.js";
import {
  MOBILE_SLOTS,
  NAV_GROUPS,
  NAV_GROUP_LABEL,
  splitForMobile,
  STAFF_MOBILE_SLOTS,
  visibleNav,
  type NavItem,
} from "./nav.js";

/**
 * القوقعة — **الإطار الذي ترثه الشاشات الثلاث والأربعون**.
 *
 * تحمل التنقّل والعنوان والهوية والوضع الليلي وحالات الشاشة. ولا تعرف شيئاً
 * عن أي شاشة بعينها: تستقبل المحتوى وترسم حوله. فالشاشة تكتب محتواها وحده،
 * وكل ما عداه مكتوبٌ هنا مرة واحدة.
 *
 * **وهي مبنيّة للهاتف أولاً**: الشريط السفلي هو الأصل، والقائمة الجانبية هي
 * ما يُضاف عند اتّساع الشاشة — لا العكس. في v2 كانت شاشةٌ واحدة من ٥٤ آمنةً
 * على الهاتف، لأن الهاتف كان يُضاف أخيراً.
 */

export type ShellUser = {
  readonly id: string;
  readonly name: string;
  /**
   * **ختمُ صورته** — و`null` تعني «لا صورة»، فيُعرَض بأوّل حرفين من اسمه.
   *
   * وكانت القوقعةُ تكتب الأحرفَ بنفسها، **فلم يكن لغير صاحب الجلسة وجهٌ
   * أصلاً** — ومكوّنُ الوجه الآن واحدٌ في النظام كلِّه (البلوبرنت §7).
   */
  readonly photoUpdatedAt: string | null;
};

export type ShellProps = {
  /** **يرى بعين موظف** — شريطٌ لا يختفي، فلا يظنّ أنه في حسابه */
  readonly viewing?: { readonly until: string } | null;
  readonly timeZone?: string;
  readonly onStopViewing?: () => void;
  readonly systemName: string;
  /** **علامةُ مِنوال** بدل أوّل حرف — لتنصيب TWING وحده */
  readonly minwalMark?: boolean;
  readonly capabilities: CapabilitySnapshot;
  readonly user: ShellUser;
  readonly current: string;
  readonly onNavigate: (key: string) => void;
  readonly mode: ThemeMode;
  readonly accent: string;
  readonly onModeChange: (mode: ThemeMode) => void;
  /**
   * **وتبديلُ اللغة يمرّ من هنا** — ولا تكتبه القوقعةُ بنفسها.
   *
   * **فكتابتُه في الجهاز وحده كانت تنقلب عليه**: لغةُ الحساب تصل مع `/me`
   * فتجد خلافاً فتُعيدها، **فيبدو الزرُّ ميتاً وهو يعمل**. والمالكُ هو من
   * يحفظها في الموضعين — كما فعل المظهرُ قبلها.
   */
  readonly onLocaleChange: (next: Locale) => void;
  /**
   * **والصورةُ تفتح الملفَّ الشخصيّ** — وكانت صورةً لا تُنقَر.
   *
   * **ومكانُ الصورة هو مدخلُ الحساب في كلّ نظامٍ يعرفه الناس**، فمن يبحث عن
   * ملفّه ينقرها أوّلاً. وبندُ القائمة موجودٌ — **ولا أحد يصل إليه**، وهو
   * عينُ ما وقع مع زرّ الخروج قبله.
   *
   * **ولا تُمرَّر في البورتال** — فلا ملفَّ شخصيّاً لجهة اتصالٍ هناك، والصورةُ
   * تبقى صورةً لا زرّاً.
   */
  readonly onOpenProfile?: () => void;
  readonly title: string;
  readonly subtitle?: string;
  /** عدّاد بجانب بند القائمة — مفتاحه مفتاح البند */
  readonly badges?: Readonly<Record<string, number>>;
  /**
   * **جرسٌ في الرأس** — للبورتال (موك أبه المعتمَد): فشريطُه السفليُّ ثلاثةٌ و«المزيد»،
   * **والتنبيهاتُ تُفتَح من الجرس** بنقطةٍ حين يكون فيها جديد.
   */
  readonly alerts?: { readonly count: number; readonly label: string; readonly onOpen: () => void };
  /**
   * **بنودُ التنقّل** — والقوقعةُ واحدة للقوقعتين.
   *
   * فالبورتالُ يمرّر بنودَه وتبقى القوقعةُ هي: نفسُ الشريط السفلي، ونفسُ
   * «المزيد»، ونفسُ تخطّي المحتوى، ونفسُ الوضع الليلي. **ونسخةٌ ثانية من
   * القوقعة** كانت ستنحرف في أوّل تعديل — وهي ما يمنعه المستودع صراحةً.
   */
  readonly items?: readonly NavItem[];
  /**
   * **الخروج** — في القوقعة، فيظهر للموظف وللعميل من موضعٍ واحد.
   *
   * وكان غائباً عن النظام كلِّه: مسارُه مكتوبٌ ومُختبَر، ونصُّه في القاموس،
   * **ولا زرَّ له في أي شاشة**. ولا بوابةَ أمسكته لأن الخروج ليس قدرةً تُمنَح
   * — فبوابةُ «كل قدرةٍ لها أثر» لا تراه. أمسكه شادي بالنقر.
   */
  readonly onSignOut: () => void;
  readonly children: ReactNode;
};

export function Shell(props: ShellProps): ReactNode {
  const { t, locale } = useLocale();
  const [moreOpen, setMoreOpen] = useState(false);

  const items = useMemo(
    () => visibleNav(props.capabilities, props.items),
    [props.capabilities, props.items],
  );
  /** **والبورتالُ يمرّر بنودَه** — فشريطُه أربعة، وشريطُ الموظف خمسة بموك أبه */
  const slots = props.items === undefined ? STAFF_MOBILE_SLOTS : MOBILE_SLOTS;
  const mobile = useMemo(() => splitForMobile(items, slots), [items, slots]);

  const go = useCallback(
    (key: string) => {
      props.onNavigate(key);
      setMoreOpen(false);
    },
    [props],
  );

  /**
   * **والقوقعةُ تُبلِّغ ولا تحفظ.**
   *
   * فالمظهرُ صار على الحساب (`users.theme`) ليمشي مع صاحبه إلى أي جهاز،
   * **وحفظُه هنا في المتصفّح كان سينقلب على نفسه**: يضغط الزرَّ فيُظلم، ثم
   * يُحدَّث نداءُ `/me` فيُعيد ما على الحساب — **فيرى اختيارَه يُلغى أمامه بلا
   * سبب**. فصاحبُ الحفظ واحدٌ، وهو من يملك عميلَ الشبكة.
   */
  const toggleMode = useCallback(() => {
    const next: ThemeMode = props.mode === "dark" ? "light" : "dark";
    props.onModeChange(next);
  }, [props]);

  return (
    <div className="app">
      {/* الوصولية من أول شاشة، لا مضافةً بعد حين */}
      <a className="skip" href="#main">
        {t("shell.skipToContent")}
      </a>

      <aside className="side">
        <div className="brand">
          {/**
           * **علامةُ مِنوال لتنصيب TWING، وأوّلُ حرفٍ للمشتري.**
           *
           * وكان الحرفُ وحده دائماً — فيرى شادي «م» بجانب «مِنوال» ويسأل أين
           * الشعار. **والمشتري يبقى بحرفه ولونه** (بيعٌ بلا علامةٍ بائعة).
           */}
          {props.minwalMark ? (
            <MinwalMark />
          ) : (
            <span className="mk" aria-hidden="true">
              {props.systemName.slice(0, 1)}
            </span>
          )}
          <b>{props.systemName}</b>
        </div>
        <nav aria-label={t("shell.mainNavigation")}>
          {NAV_GROUPS.map((group) => {
            const groupItems = items.filter((i) => i.group === group);
            if (groupItems.length === 0) return null;
            return (
              <div key={group}>
                <div className="sgrp">{t(NAV_GROUP_LABEL[group])}</div>
                {groupItems.map((item) => (
                  <NavButton
                    key={item.key}
                    item={item}
                    current={props.current}
                    onGo={go}
                    count={props.badges?.[item.key] ?? 0}
                  />
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="col">
        <header className="top">
          <div className="ttl">
            {props.title}
            {props.subtitle ? <span className="sb">{props.subtitle}</span> : null}
          </div>
          {props.alerts ? (
            <button
              type="button"
              className="icb bell"
              onClick={props.alerts.onOpen}
              aria-label={props.alerts.label}
              title={props.alerts.label}
            >
              <Icon name="bell" />
              {props.alerts.count > 0 ? <span className="bell-dot" aria-hidden="true" /> : null}
            </button>
          ) : null}
          <button
            type="button"
            className="icb"
            onClick={toggleMode}
            aria-label={t(props.mode === "dark" ? "shell.themeLight" : "shell.themeDark")}
          >
            <Icon name={props.mode === "dark" ? "sun" : "moon"} />
          </button>
          <button
            type="button"
            className="icb lang"
            onClick={() => {
              props.onLocaleChange(locale === "ar" ? "en" : "ar");
            }}
            aria-label={t("shell.switchLanguage")}
          >
            {locale === "ar" ? "EN" : "ع"}
          </button>
          {props.onOpenProfile ? (
            <button
              type="button"
              className="icb avb"
              onClick={props.onOpenProfile}
              aria-label={t("nav.profile")}
              title={t("nav.profile")}
            >
              <PersonPhoto
                userId={props.user.id}
                name={props.user.name}
                stamp={props.user.photoUpdatedAt}
              />
            </button>
          ) : (
            <PersonPhoto
              userId={props.user.id}
              name={props.user.name}
              stamp={props.user.photoUpdatedAt}
            />
          )}
          <button
            type="button"
            className="icb"
            onClick={props.onSignOut}
            aria-label={t("auth.signOut")}
            title={t("auth.signOut")}
          >
            <Icon name="logout" />
          </button>
        </header>

        <main id="main" tabIndex={-1}>
          {/**
           * **الانقطاعُ وعرضُ التثبيت فوق كل شاشة** — في القوقعة الواحدة،
           * فتَرِثهما الشاشاتُ الثلاثُ والأربعون بلا أن تكتبهما واحدة.
           */}
          {props.viewing ? (
            <div className="banner info viewing-bar" role="status">
              <span>
                {t("preview.banner", {
                  name: props.user?.name ?? "",
                  until: formatTime(new Date(props.viewing.until), locale, props.timeZone ?? "UTC"),
                })}
              </span>
              <button type="button" className="btn sm" onClick={props.onStopViewing}>
                {t("preview.exit")}
              </button>
            </div>
          ) : null}
          <InstallBar />
          {props.children}
        </main>
      </div>

      <nav className="bot" aria-label={t("shell.quickNavigation")}>
        {mobile.bar.map((item) => (
          <BottomButton
            key={item.key}
            item={item}
            current={props.current}
            onGo={go}
            t={t}
            count={props.badges?.[item.key] ?? 0}
          />
        ))}
        {mobile.more.length > 0 ? (
          <button
            type="button"
            aria-expanded={moreOpen}
            aria-current={mobile.more.some((i) => i.key === props.current) ? "page" : undefined}
            onClick={() => {
              setMoreOpen((open) => !open);
            }}
          >
            <Icon name="more" />
            {t("nav.more")}
          </button>
        ) : null}
      </nav>

      {moreOpen ? (
        <div className="more-sheet" role="dialog" aria-label={t("nav.more")}>
          <div className="more-head">
            <b>{t("nav.more")}</b>
            <button
              type="button"
              className="icb"
              data-close
              aria-label={t("common.close")}
              onClick={() => {
                setMoreOpen(false);
              }}
            >
              <Icon name="close" />
            </button>
          </div>
          {mobile.more.map((item) => (
            <NavButton
              key={item.key}
              item={item}
              current={props.current}
              onGo={go}
              count={props.badges?.[item.key] ?? 0}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * **وكلُّ عدّادٍ يقول ما يعدّه** — فقارئُ الشاشة لا يسمع «غير مقروءة» على
 * «اللي عليك». والافتراضيُّ اسمُ عدّاد الإشعارات، **فبندٌ جديدٌ بعدّاد يُقال بمعناه
 * حين يُضاف هنا**.
 */
const BADGE_LABEL: Readonly<Record<string, TranslationKey>> = {
  actions: "nav.actionsBadge",
};

function badgeLabel(key: string): TranslationKey {
  return BADGE_LABEL[key] ?? "notifications.unreadCount";
}

function NavButton({
  item,
  current,
  onGo,
  count = 0,
}: {
  readonly item: NavItem;
  readonly current: string;
  readonly onGo: (key: string) => void;
  readonly count?: number;
}): ReactNode {
  const { t } = useLocale();
  return (
    <button
      type="button"
      className="nav"
      aria-current={item.key === current ? "page" : undefined}
      onClick={() => {
        onGo(item.key);
      }}
    >
      <Icon name={item.icon} />
      {t(item.labelKey)}
      {count > 0 ? (
        <span className="bdg n" aria-label={t(badgeLabel(item.key))}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

function BottomButton({
  item,
  current,
  onGo,
  t,
  count = 0,
}: {
  readonly item: NavItem;
  readonly current: string;
  readonly onGo: (key: string) => void;
  readonly t: ReturnType<typeof useLocale>["t"];
  /** **وعدّادُ البند على الشريط نفسِه** — من نفس `badges` التي تقرؤها القائمة */
  readonly count?: number;
}): ReactNode {
  return (
    <button
      type="button"
      aria-current={item.key === current ? "page" : undefined}
      onClick={() => {
        onGo(item.key);
      }}
    >
      <Icon name={item.icon} />
      {t(item.labelKey)}
      {count > 0 ? (
        <span className="bdg n" aria-label={t(badgeLabel(item.key))}>
          {count}
        </span>
      ) : null}
    </button>
  );
}
